// API
import { api } from '../configs/api/api';

// Models
import {
  ICompanyProfile, ICreateSpecialtySpecialisations, ISpecialtySpecialisations,
  IUpdateCompanyBusinessAddress,
  IUpdateCompanyContactDetails, IUpdateSpecialtySpecialisations,
} from '../interfaces/company.interfaces';
import { ICountry, ILanguage } from '../interfaces/core.interfaces';

export const CompanyService = {
  fetchCompanyProfile: async (): Promise<{ company_profile?: ICompanyProfile, error?: string }> => {
    try {
      const axiosResponse = await api.get('/company/company-profile', {});
      return axiosResponse.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanyName: async (company_name: string): Promise<{
    company_profile?: ICompanyProfile,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/update-company-name', { company_name });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanyWebUrl: async (url_code: string): Promise<{
    company_profile?: ICompanyProfile,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/update-company-url', { url_code });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanyAbout: async (about: string): Promise<{
    company_profile?: ICompanyProfile,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/update-company-about', { about });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanyContactDetails: async (data: IUpdateCompanyContactDetails): Promise<{
    company_profile?: ICompanyProfile,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/update-contact-details', data);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanyBusinessAddress: async (data: IUpdateCompanyBusinessAddress): Promise<{
    company_profile?: ICompanyProfile,
    error?: string
  }> => {
    try {
      const response = await api.post('/company/update-address', data);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  fetchStipeAccountLink: async (data: { return_url: string, refresh_url: string }): Promise<{
    account_url?: string,
    error?: string
  }> => {
    try {
      const response = await api.post('/workers/stripe-account', data);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  connectStripeToCompany: async (account_id: string): Promise<{
    is_stripe_connected?: boolean,
    error?: string
  }> => {
    try {
      const response = await api.get(`/workers/stripe-account/return/${ account_id }`);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanyCountries: async (countries: string[]): Promise<{
    countries?: ICountry[],
    error?: string
  }> => {
    try {
      const response = await api.post(`/company/update-countries`, {
        countries
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanyLanguages: async (languages: string[]): Promise<{
    languages?: ICountry[],
    error?: string
  }> => {
    try {
      const response = await api.post(`/company/update-languages`, {
        languages
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  createCompanySpecialtySpecialisations: async (data: ICreateSpecialtySpecialisations): Promise<{
    company_specializations?: ISpecialtySpecialisations,
    error?: string
  }> => {
    try {
      const response = await api.post(`/company/create-specializations`, data);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  updateCompanySpecialtySpecialisations: async (data: IUpdateSpecialtySpecialisations): Promise<{
    company_specializations?: ISpecialtySpecialisations,
    error?: string
  }> => {
    try {
      const response = await api.post(`/company/update-specializations`, data);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  deleteCompanySpecialtySpecialisations: async (speciality_id: number): Promise<{
    company_specializations?: ISpecialtySpecialisations,
    error?: string
  }> => {
    try {
      const response = await api.delete(`/company/delete-speciality`, {
        params: { speciality_id }
      });
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  fetchCompanySpecialtySpecialisations: async (): Promise<{
    company_specializations?: ISpecialtySpecialisations[],
    error?: string
  }> => {
    try {
      const response = await api.get(`/company/specializations`);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  fetchCompanyAllAvailableCountries: async (): Promise<{
    countries?: ICountry[],
    error?: string
  }> => {
    try {
      const response = await api.get(`/countries`);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
  fetchCompanyAllAvailableLanguages: async (): Promise<{
    languages?: ILanguage[],
    error?: string
  }> => {
    try {
      const response = await api.get(`/languages`);
      return response.data;
    } catch (error: any) {
      const res = error?.response?.data;
      if (error?.response.status === 500) {
        return { error: res?.message };
      }
      return { ...res };
    }
  },
}
